"use client"

import { Navigate } from "react-router"

import { PrismClinicalDashboard } from "@/components/prism-clinical-dashboard"
import { PrismDemoScreenFrame } from "@/components/prism-demo-screen-frame"
import { PrimaryPageTemplate } from "@/components/templates/primary-page-template"
import { useLoginSession } from "@/hooks/use-login-session"
import { useProduct } from "@/contexts/product-context"
import type { PrismDemoScreenId } from "@/lib/prism-demo-screens"
import { productSlug } from "@/stores/app-store"

import { getPrismNavMode } from "@/hooks/use-prism-nav-mode"
import { ExternalDemoIframeFrame } from "@/components/external-demo-iframe-frame"
import { oneSiteDemoHostedFrame } from "@/lib/one-site-demo"

import Dashboard from "./dashboard"

function clinicalEducationDashboardScreen(
  role: ReturnType<typeof useLoginSession>["role"],
): PrismDemoScreenId {
  if (role === "student") return "student-dashboard"
  if (role === "member") return "faculty-dashboard"
  return "clinical-dashboard"
}

function usesClinicalDashboardDemo(role: ReturnType<typeof useLoginSession>["role"]): boolean {
  return role === "student" || role === "member" || role === "administrator"
}

/**
 * Product dashboard entry.
 *
 * Clinical Education (and custom tenants that inherit Prism IA) use the
 * three-area module launcher. Compliance redirects to Student. Other products
 * keep the shared chart shell.
 */
export default function ProductDashboard() {
  const { product } = useProduct()
  const session = useLoginSession()
  const oneSiteFrame = oneSiteDemoHostedFrame(product)
  if (oneSiteFrame) {
    return <ExternalDemoIframeFrame src={oneSiteFrame.src} title={oneSiteFrame.title} />
  }
  if (product === "exxat-compliance") {
    if (session.role === "student") {
      return <Navigate to="/compliance/student" replace />
    }
    if (session.role === "member") {
      return <Navigate to="/compliance/faculty" replace />
    }
    return <Navigate to="/compliance/student" replace />
  }
  if (product === "exxat-courses") {
    return <Navigate to="/courses" replace />
  }
  if (product === "exxat-programs") {
    return <Navigate to="/programs" replace />
  }
  if (product === "exxat-student-success") {
    return <Navigate to="/student-success/competency-management" replace />
  }
  if (product === "exxat-curriculum-mapping") {
    return <Navigate to="/curriculum-mapping/curriculum-mapping" replace />
  }
  if (product === "exxat-one-schools") {
    return <PrismDemoScreenFrame screen="one-school-dashboard" title="Dashboard" />
  }
  if (product === "exxat-prism" || product === "exxat-custom") {
    if (usesClinicalDashboardDemo(session.role)) {
      return (
        <PrismDemoScreenFrame
          screen={clinicalEducationDashboardScreen(session.role)}
          title="Dashboard"
        />
      )
    }
    if (getPrismNavMode() === "sidebar") {
      return <Navigate to="../program-details" replace relative="path" />
    }
    return (
      <PrimaryPageTemplate
        siteHeader={{ title: "Dashboard" }}
        bodyClassName="theme-prism bg-muted"
      >
        <PrismClinicalDashboard productRoot={productSlug(product)} />
      </PrimaryPageTemplate>
    )
  }
  return <Dashboard />
}
